import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid, CircularProgress, Box, Typography } from '@mui/material';
import { useApiGet, goClient, formGoClient } from '../../../utils/httpClientTs';
import {
  getIRSFormQuestions,
  getIRSSectionMetadata,
} from '../../../endpoints/irsForms';
import { IRSQuestion, IRSSectionMetadata } from '../../../types/irsForms';
import { formatCurrency } from '../../../utils/formatters';
import IRSFormQuestion from './IRSFormQuestion';

// Simple debounce function
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

interface IRSFormSectionProps {
  formId: string;
  sectionId: string;
  editable: boolean;
  formType: 'lc' | 'sc' | 'rg';
  isActive?: boolean;
}

const IRSFormSection: React.FC<IRSFormSectionProps> = ({
  formId,
  sectionId,
  editable,
  formType,
  isActive = false,
}) => {
  const [questions, setQuestions] = useState<IRSQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [saveStatus, setSaveStatus] = useState<
    Record<string, 'idle' | 'saving' | 'saved' | 'error'>
  >({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const pendingSavesRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const savedTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  const {
    data: questionsData,
    isLoading,
    refetch,
  } = useApiGet(
    ['irs-form-questions', formId, sectionId],
    () => getIRSFormQuestions(formId, sectionId),
    {
      enabled: !!formId && !!sectionId,
      retry: 1,
    }
  );

  // Special section ID that requires metadata comparison
  const SPECIAL_SECTION_ID = '3f7dc6636fd041d8baa973f234d27955';
  const QUESTION_1_ID = 'd7c70222eba541f196aab0e1157f2bc9';

  // Fetch metadata for special section
  const { data: sectionMetadata } = useApiGet<IRSSectionMetadata>(
    ['irs-section-metadata', formId, sectionId],
    () => getIRSSectionMetadata(formId, sectionId),
    {
      enabled:
        !!formId && !!sectionId && sectionId === SPECIAL_SECTION_ID && isActive,
      retry: 1,
    }
  );

  // Check if values don't match
  const question1Value = answers[QUESTION_1_ID];
  const showMismatchWarning =
    sectionId === SPECIAL_SECTION_ID &&
    sectionMetadata &&
    question1Value !== null &&
    question1Value !== undefined &&
    Math.abs(Number(question1Value) - sectionMetadata.roster_dues_total) > 0.01; // Allow small floating point differences

  useEffect(() => {
    if (questionsData) {
      setQuestions(questionsData);
      // Initialize answers from questions data
      const initialAnswers: Record<string, any> = {};
      questionsData.forEach((q) => {
        if (q.decimal_answer !== null && q.decimal_answer !== undefined) {
          initialAnswers[q.question_id] = q.decimal_answer;
        } else if (
          q.integer_answer !== null &&
          q.integer_answer !== undefined
        ) {
          initialAnswers[q.question_id] = q.integer_answer;
        } else if (q.text_answer !== null && q.text_answer !== undefined) {
          initialAnswers[q.question_id] = q.text_answer;
        } else if (
          q.boolean_answer !== null &&
          q.boolean_answer !== undefined
        ) {
          initialAnswers[q.question_id] = q.boolean_answer;
        } else if (q.date_answer) {
          initialAnswers[q.question_id] = q.date_answer;
        } else if (q.document_id) {
          initialAnswers[q.question_id] = q.document_id;
        } else {
          initialAnswers[q.question_id] = null;
        }
      });
      setAnswers(initialAnswers);
    }
  }, [questionsData]);

  // Refetch data when section becomes active (tab is switched to)
  useEffect(() => {
    if (isActive && formId && sectionId) {
      refetch();
    }
  }, [isActive, formId, sectionId, refetch]);

  // Debounced save function
  const debouncedSaveRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Save function with comprehensive state management
  const performSave = useCallback(
    async (
      questionId: string,
      value: any,
      answerType: string,
      signal?: AbortSignal
    ) => {
      // Cancel any existing timeout for this question
      if (savedTimeoutsRef.current[questionId]) {
        clearTimeout(savedTimeoutsRef.current[questionId]);
        delete savedTimeoutsRef.current[questionId];
      }

      // Set status to saving
      setSaveStatus((prev) => ({ ...prev, [questionId]: 'saving' }));
      setSaveErrors((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
      pendingSavesRef.current.add(questionId);

      try {
        // Handle image type with file upload
        if (answerType === 'image' && value instanceof File) {
          // Upload file using FormData
          const formData = new FormData();
          formData.append('file', value);
          formData.append('document_type', 'supporting_document');

          await formGoClient.put(
            `/api/annual-reports/irs/${formId}/questions/${questionId}/answer`,
            formData,
            {
              signal,
            }
          );
        } else {
          // Regular answer update for other types
          const updateData: any = {};
          if (answerType === 'decimal') {
            updateData.decimal_answer =
              value !== null && value !== '' ? parseFloat(String(value)) : null;
          } else if (answerType === 'integer') {
            updateData.integer_answer =
              value !== null && value !== ''
                ? parseInt(String(value), 10)
                : null;
          } else if (answerType === 'text') {
            updateData.text_answer = value || null;
          } else if (answerType === 'boolean') {
            updateData.boolean_answer = value;
          } else if (answerType === 'date') {
            updateData.date_answer = value || null;
          } else if (answerType === 'image') {
            // For image type, value is document_id (string) or null
            updateData.document_id = value || null;
          }

          // Make API call with signal support
          await goClient.put(
            `/api/annual-reports/irs/${formId}/questions/${questionId}/answer`,
            updateData,
            {
              signal,
            }
          );
        }

        // Success: set status to saved, remove from pending
        setSaveStatus((prev) => ({ ...prev, [questionId]: 'saved' }));
        pendingSavesRef.current.delete(questionId);

        // Auto-transition saved to idle after 2 seconds
        savedTimeoutsRef.current[questionId] = setTimeout(() => {
          setSaveStatus((prev) => {
            const updated = { ...prev };
            if (updated[questionId] === 'saved') {
              updated[questionId] = 'idle';
            }
            return updated;
          });
          delete savedTimeoutsRef.current[questionId];
        }, 2000);
      } catch (error: any) {
        // Check if error is due to abort
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          // Request was cancelled, don't update status
          return;
        }

        // Error: set status to error, store error message, remove from pending
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save answer. Please try again.';
        setSaveStatus((prev) => ({ ...prev, [questionId]: 'error' }));
        setSaveErrors((prev) => ({ ...prev, [questionId]: errorMessage }));
        pendingSavesRef.current.delete(questionId);
        console.error('Failed to save answer:', error);
      }
    },
    [formId]
  );

  useEffect(() => {
    debouncedSaveRef.current = debounce(
      async (questionId: string, value: any, answerType: string) => {
        // Cancel any existing request for this question
        if (abortControllersRef.current[questionId]) {
          abortControllersRef.current[questionId].abort();
        }

        // Create new AbortController for this save
        const abortController = new AbortController();
        abortControllersRef.current[questionId] = abortController;

        await performSave(
          questionId,
          value,
          answerType,
          abortController.signal
        );
      },
      500
    );

    return () => {
      // Cleanup on unmount: cancel all pending requests
      // Capture ref values to avoid stale closure issues
      const abortControllers = abortControllersRef.current;
      const savedTimeouts = savedTimeoutsRef.current;
      const pendingSaves = pendingSavesRef.current;

      Object.values(abortControllers).forEach((controller) => {
        controller.abort();
      });
      Object.values(savedTimeouts).forEach((timeout) => {
        clearTimeout(timeout);
      });
      // Clear the captured Set
      pendingSaves.clear();
      // Reset refs
      abortControllersRef.current = {};
      savedTimeoutsRef.current = {};
      pendingSavesRef.current = new Set();
    };
  }, [performSave]);

  const handleAnswerChange = (
    questionId: string,
    value: any,
    answerType: string
  ) => {
    // Optimistic UI update - update immediately
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (editable && debouncedSaveRef.current) {
      debouncedSaveRef.current(questionId, value, answerType);
    }
  };

  // Retry save function
  const retrySave = useCallback(
    (questionId: string) => {
      const question = questions.find((q) => q.question_id === questionId);
      if (!question) return;

      const value = answers[questionId];
      // Clear error state before retry
      setSaveErrors((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
      // Cancel any existing request for this question
      if (abortControllersRef.current[questionId]) {
        abortControllersRef.current[questionId].abort();
      }
      // Create new AbortController for retry
      const abortController = new AbortController();
      abortControllersRef.current[questionId] = abortController;
      // Trigger save immediately (bypass debounce for retry)
      if (editable) {
        performSave(
          questionId,
          value,
          question.answer_type,
          abortController.signal
        );
      }
    },
    [questions, answers, editable, performSave]
  );

  const handleOtherItemsUpdate = () => {
    // Only refetch when explicitly needed (e.g., other_line_items update)
    refetch();
  };

  // Navigation protection - warn if pending saves exist
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingSavesRef.current.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Group questions by parent/child relationships
  const getQuestionGroups = () => {
    const parentQuestions: IRSQuestion[] = [];
    const childQuestionsMap: Record<string, IRSQuestion[]> = {};

    questions.forEach((q) => {
      if (!q.parent_question_id) {
        parentQuestions.push(q);
      } else {
        if (!childQuestionsMap[q.parent_question_id]) {
          childQuestionsMap[q.parent_question_id] = [];
        }
        childQuestionsMap[q.parent_question_id].push(q);
      }
    });

    // Sort parent questions by display_order
    parentQuestions.sort((a, b) => {
      const orderA = a.display_order || 0;
      const orderB = b.display_order || 0;
      return orderA - orderB;
    });

    // Sort child questions by display_order
    Object.keys(childQuestionsMap).forEach((parentId) => {
      childQuestionsMap[parentId].sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });
    });

    return { parentQuestions, childQuestionsMap };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const { parentQuestions, childQuestionsMap } = getQuestionGroups();

  return (
    <Grid container spacing={2}>
      {parentQuestions.map((question) => {
        const childQuestions = childQuestionsMap[question.question_id] || [];
        const parentAnswer = answers[question.question_id];

        return (
          <Grid item xs={12} key={question.question_id}>
            <IRSFormQuestion
              question={question}
              answer={answers[question.question_id]}
              onChange={(value) =>
                handleAnswerChange(
                  question.question_id,
                  value,
                  question.answer_type
                )
              }
              disabled={
                !editable || saveStatus[question.question_id] === 'saving'
              }
              parentAnswer={parentAnswer}
              formId={formId}
              onOtherItemsUpdate={handleOtherItemsUpdate}
              saveStatus={saveStatus[question.question_id] || 'idle'}
              saveError={saveErrors[question.question_id]}
              onRetrySave={() => retrySave(question.question_id)}
            />
            {/* Warning for roster dues mismatch on question 1 */}
            {question.question_id === QUESTION_1_ID && showMismatchWarning && (
              <Typography
                variant="body2"
                sx={{
                  color: 'error.main',
                  mt: 1,
                  ml: 1,
                  fontStyle: 'italic',
                }}
              >
                Note: The Roster and Dues total (
                {formatCurrency(sectionMetadata.roster_dues_total)}) does not
                match this value ({formatCurrency(Number(question1Value))}).
                Please verify these values are correct.
              </Typography>
            )}
            {/* Render child questions */}
            {childQuestions.map((childQuestion) => (
              <Box key={childQuestion.question_id} sx={{ ml: 4, mt: 1 }}>
                <IRSFormQuestion
                  question={childQuestion}
                  answer={answers[childQuestion.question_id]}
                  onChange={(value) =>
                    handleAnswerChange(
                      childQuestion.question_id,
                      value,
                      childQuestion.answer_type
                    )
                  }
                  disabled={
                    !editable ||
                    saveStatus[childQuestion.question_id] === 'saving'
                  }
                  parentAnswer={parentAnswer}
                  formId={formId}
                  onOtherItemsUpdate={handleOtherItemsUpdate}
                  saveStatus={saveStatus[childQuestion.question_id] || 'idle'}
                  saveError={saveErrors[childQuestion.question_id]}
                  onRetrySave={() => retrySave(childQuestion.question_id)}
                />
              </Box>
            ))}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default IRSFormSection;
